// src/pages/Dashboard/student/chemistry/ExperimentView.jsx
import { useState, useEffect, useMemo, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSearchParams } from "react-router-dom";

import Beaker from "./Beaker";
import TestTube from "./TestTube";
import Flask from "./Flask";
import StepPopup from "../StepPopup";
import ReagentBottle from "../ReagentBottle";
import ReactionLog from "../ReactionLog";
import StepProgress from "../StepProgress";
import ConfettiEffect from "../ConfettiEffect";
import DropZone from "../Dropzone";
import CHEMISTRY_EXPERIMENTS from "../../../data/chemistryExperiments";

export default function ExperimentView({ experimentId, onBack }) {
  const [searchParams] = useSearchParams();
  const selectedClass = searchParams.get("class");

  const exp = useMemo(() => {
    if (!selectedClass) return null;
    const experiments = CHEMISTRY_EXPERIMENTS[selectedClass];
    if (!experiments) return null;
    const found = experiments.find((e) => e.id === experimentId);
    return found ? { ...found, classLevel: parseInt(selectedClass, 10) } : null;
  }, [experimentId, selectedClass]);

  const [log, setLog] = useState([]);
  const [reactionComplete, setReactionComplete] = useState(false);
  const [colorState, setColorState] = useState("blue");
  const [popup, setPopup] = useState(null);
  const [reagentStep, setReagentStep] = useState(0);
  const [toolStep, setToolStep] = useState(0);
  const [effectStep, setEffectStep] = useState(0);
  const [currentEffect, setCurrentEffect] = useState(null);

  const [reactionTimer, setReactionTimer] = useState(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const countdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const INTERACTIVE_MODE = true;

  useEffect(() => {
    if (exp) {
      setLog([]);
      setReactionComplete(false);
      setColorState("blue");
      setPopup({ type: "info", message: exp.steps?.[0] ?? "Start experiment" });
      setReagentStep(0);
      setToolStep(0);
      setEffectStep(0);
      setCurrentEffect(null);
      setReactionTimer(null);
      setIsWaiting(false);
    }
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [exp, experimentId]);

  if (!exp)
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 font-semibold">
          Experiment not found for "{experimentId}"
        </p>
        <button onClick={onBack} className="mt-3 text-blue-600 underline">
          ← Back to Lab
        </button>
      </div>
    );

  const normalizeColor = (colorString) => {
    if (!colorString) return "blue";
    const s = colorString.toLowerCase();

    if (s.includes("red")) return "red";
    if (s.includes("green")) return "green";
    if (s.includes("pink") || s.includes("purple")) return "pink";
    if (s.includes("gray") || s.includes("grey")) return "gray";
    if (s.includes("orange") || s.includes("amber")) return "orange";
    if (s.includes("blue")) return "blue";
    if (s.includes("brown")) return "brown";

    if (s.startsWith("#")) {
      const hex = s.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      if (r > 180 && g < 80 && b < 80) return "red";
      if (r < 80 && g > 180 && b < 80) return "green";
      if (r < 80 && g < 80 && b > 180) return "blue";
      if (r > 200 && g > 160 && b < 80) return "orange";
      if (r > 200 && g > 200 && b > 200) return "gray";
      if (r > 150 && g < 100 && b < 80) return "brown";

      return "blue";
    }

    return "blue";
  };

  const findItemById = (id) =>
    exp.reagents?.find((r) => r.id === id) ||
    exp.tools?.find((t) => t.id === id) ||
    exp.effects?.find((e) => e.id === id) ||
    null;

  const finishStepEffect = (category, msg) => {
    const hasBubbles =
      exp.effect === "bubbles" || exp.effects?.some((e) => e.id === "bubbles");
    const hasFire =
      exp.effect === "fire" || exp.effects?.some((e) => e.id === "fire");
    const hasSmoke =
      exp.effect === "smoke" || exp.effects?.some((e) => e.id === "smoke");

    if (hasBubbles)
      setPopup({ type: "success", message: "🫧 Bubbles observed!" });
    else if (hasFire)
      setPopup({ type: "success", message: "🔥 Combustion observed!" });
    else if (hasSmoke)
      setPopup({ type: "success", message: "💨 Smoke / fumes observed!" });
    else setPopup({ type: "success", message: `✅ ${msg}` });

    if (category === "reagent") {
      setReagentStep((prev) => {
        const next = prev + 1;
        const done = next >= (exp.reagents?.length ?? 0);
        const toolDone = toolStep >= (exp.tools?.length ?? 0);
        const effectDone = effectStep >= (exp.effects?.length ?? 0);
        if (done && toolDone && effectDone) finishReaction();
        return next;
      });
    } else if (category === "tool") {
      setToolStep((prev) => {
        const next = prev + 1;
        const reagentDone = reagentStep >= (exp.reagents?.length ?? 0);
        const done = next >= (exp.tools?.length ?? 0);
        const effectDone = effectStep >= (exp.effects?.length ?? 0);
        if (reagentDone && done && effectDone) finishReaction();
        return next;
      });
    } else if (category === "effect") {
      setEffectStep((prev) => {
        const next = prev + 1;
        const reagentDone = reagentStep >= (exp.reagents?.length ?? 0);
        const toolDone = toolStep >= (exp.tools?.length ?? 0);
        const done = next >= (exp.effects?.length ?? 0);
        if (reagentDone && toolDone && done) finishReaction();
        return next;
      });
    }
  };

  const handleDrop = (item, category) => {
    if (!item || !item.id) return;
    const reagentId = item.id;

    if (reactionComplete || isWaiting) return;

    const obj = findItemById(reagentId);
    if (!obj) return;

    // COLOR CHANGE SHOULD ONLY OCCUR FROM EFFECTS
    if (item.category === "effect" && item.id === "colorChange" && item.color) {
      setColorState(normalizeColor(item.color));
    }

    // Effects: when dropping into Effect zone
    if (category === "effect") {
      // "bubbles" | "smoke" | "fire" | "colorChange" etc.
      setCurrentEffect(reagentId);
    }

    const msg = exp.outputs?.[reagentId] || `${obj.name} used`;
    setLog((prev) => [...prev, msg]);

    let waitDuration = 0;
    if (obj.wait && obj.wait > 0) waitDuration = obj.wait;
    else if (exp.waitTime && exp.waitTime > 0) waitDuration = exp.waitTime;

    if (waitDuration > 10 && INTERACTIVE_MODE) waitDuration = 3;

    // 🔹 KEY FIX: if no wait time but this is an EFFECT,
    // show the visual (bubbles/smoke/fire) for ~1.5s before finishing.
    if (!waitDuration) {
      if (category === "effect") {
        const EFFECT_DISPLAY_MS = 1500;

        setIsWaiting(true);
        setPopup({
          type: "info",
          message: "🧪 Reaction in progress...",
        });

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        timeoutRef.current = setTimeout(() => {
          setIsWaiting(false);
          timeoutRef.current = null;
          finishStepEffect(category, msg);
        }, EFFECT_DISPLAY_MS);
      } else {
        // reagents/tools: keep old instant behaviour
        finishStepEffect(category, msg);
      }
      return;
    }

    if (waitDuration <= 10) {
      setIsWaiting(true);
      setReactionTimer(waitDuration);
      setPopup({
        type: "info",
        message: `⏳ Reaction in ${waitDuration}s...`,
      });

      let remaining = waitDuration;
      countdownRef.current = setInterval(() => {
        remaining -= 1;
        setReactionTimer(remaining);
        setPopup({
          type: "info",
          message: `⏳ Reaction in progress: ${remaining}s remaining...`,
        });

        if (remaining <= 0) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          setIsWaiting(false);
          setReactionTimer(null);
          finishStepEffect(category, msg);
        }
      }, 1000);

      return;
    }

    setIsWaiting(true);
    setPopup({
      type: "info",
      message: `⏳ Reaction in progress... Please wait around ${waitDuration}s.`,
    });

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    timeoutRef.current = setTimeout(() => {
      setIsWaiting(false);
      setReactionTimer(null);
      timeoutRef.current = null;
      finishStepEffect(category, msg);
    }, waitDuration * 1000);
  };

  const finishReaction = () => {
    setReactionComplete(true);
    setTimeout(
      () =>
        setPopup({
          type: "success",
          message: "<strong>✨ Reaction complete!</strong>",
        }),
      500
    );
  };

  const reset = () => {
    setLog([]);
    setColorState("blue");
    setReactionComplete(false);
    setReagentStep(0);
    setToolStep(0);
    setEffectStep(0);
    setPopup({ type: "info", message: exp.steps?.[0] ?? "Start experiment" });

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsWaiting(false);
    setReactionTimer(null);
    setCurrentEffect(null);
  };

  const renderApparatus = () => {
    const commonProps = {
      onDrop: (item) => handleDrop(item, "reagent"),
      colorState,
      reactionComplete,
      activeEffect: currentEffect,
    };

    switch (exp.apparatus) {
      case "test_tube":
        return <TestTube {...commonProps} />;
      case "flask":
        return <Flask {...commonProps} />;
      default:
        return <Beaker {...commonProps} />;
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col md:flex-row h-screen md:h-[90vh] bg-gradient-to-br from-sky-50 to-indigo-50 overflow-hidden">
        {/* Sidebar */}
        <aside className="md:w-72 w-full bg-white/90 backdrop-blur border-b md:border-b-0 md:border-r border-gray-200 p-5 flex flex-col md:h-auto h-[40vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onBack}
              className="text-sm text-blue-700 font-medium hover:underline"
            >
              ← Back
            </button>
            <button
              onClick={reset}
              className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200"
            >
              Reset
            </button>
          </div>

          <h2 className="font-bold text-lg text-blue-700 mb-1">{exp.title}</h2>
          <div className="text-xs text-gray-500 mb-3">
            Class {exp.classLevel}
          </div>

          <h3 className="font-semibold text-gray-700 mb-2">🧭 Steps</h3>
          <StepProgress
            steps={exp.steps}
            current={Math.min(
              reagentStep + toolStep + effectStep,
              exp.steps.length - 1
            )}
          />

          {exp.reagents?.length > 0 && (
            <div className="mt-5">
              <h3 className="font-semibold text-gray-700 mb-2">🧪 Reagents</h3>
              <div className="grid grid-cols-2 gap-3">
                {exp.reagents.map((r, idx) => (
                  <ReagentBottle
                    key={r.id}
                    reagent={r}
                    category="reagent"
                    disabled={
                      reactionComplete || idx !== reagentStep || isWaiting
                    }
                    isExpected={idx === reagentStep && !reactionComplete}
                  />
                ))}
              </div>
            </div>
          )}

          {exp.tools?.length > 0 && (
            <div className="mt-5">
              <h3 className="font-semibold text-gray-700 mb-2">⚙️ Tools</h3>
              <div className="grid grid-cols-2 gap-3">
                {exp.tools.map((t, idx) => (
                  <ReagentBottle
                    key={t.id}
                    reagent={t}
                    category="tool"
                    disabled={reactionComplete || idx !== toolStep || isWaiting}
                    isExpected={idx === toolStep && !reactionComplete}
                  />
                ))}
              </div>
            </div>
          )}

          {exp.effects?.length > 0 && (
            <div className="mt-5 mb-6">
              <h3 className="font-semibold text-gray-700 mb-2">💨 Effects</h3>
              <div className="grid grid-cols-2 gap-3">
                {exp.effects.map((e, idx) => (
                  <ReagentBottle
                    key={e.id}
                    reagent={e}
                    category="effect"
                    disabled={
                      reactionComplete || idx !== effectStep || isWaiting
                    }
                    isExpected={idx === effectStep && !reactionComplete}
                  />
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Main Area */}
        <main className="flex-1 flex flex-col items-center justify-center relative overflow-hidden">
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-sm pointer-events-auto">
            <StepPopup popup={popup} />
          </div>

          <div className="absolute inset-0 z-40 pointer-events-none">
            <ConfettiEffect active={reactionComplete} />
          </div>

          <div className="relative z-30 flex flex-col items-center justify-center w-full h-full scale-[0.9] transition-transform origin-center">
            {/* Effects drop area */}
            <div className="mb-2 flex items-center justify-center min-h-[10px] mt-2">
              <DropZone
                acceptType="effect"
                onDrop={(item) => handleDrop(item, "effect")}
                label="Drop Effects Here (e.g. bubbles, smoke, fire)"
              />
            </div>

            {/* Apparatus */}
            {renderApparatus()}

            {/* Tools drop */}
            <div className="mt-3 flex items-center justify-center min-h-[70px]">
              <DropZone
                acceptType="tool"
                onDrop={(item) => handleDrop(item, "tool")}
                label="Drop Tools Here (heat, electricity, etc.)"
              />
            </div>

            {/* Log */}
            <div className="mt-4 w-[70%] max-w-xl">
              <ReactionLog log={log} />
              <div className="mt-4 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Tip: Drag reagents into the apparatus first, then tools, then
                  effects.
                </div>
                <button
                  onClick={onBack}
                  className="text-sm bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-all"
                >
                  Exit Experiment ✖
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </DndProvider>
  );
}
